import React from 'react';
import { Button } from 'primereact/button';

import CaseFilter from './CaseFilter';
import FileTypeFilter from './FileTypeFilter';
import TagFilter from './TagFilter';
import ResetFilters from './ResetFilters';
import FileStatusFilter from './FileStatusFilter';
import UploadedByFilter from './UploadedByFilter';
import PracticeAreaFilter from './PracticeAreaFilter';
import LeadAttorneyFilter from './LeadAttorneyFilter';
import LeadParalegalFilter from './LeadParalegalFilter';
import ReadStatusFilter from './ReadStatusFilter';
import SignatureStatusFilter from './SignatureStatusFilter';
import DateRangeFilter from './DateRangeFilter';
import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import useFilterBreakpoint from 'hooks/useFilterBreakpoint';
import '../../../../../assets/scss/FilterResponsive.scss';

function Filters() {
  const { isClientFileManager, isCaseFileManager, isFirmDocuments, isLeadFileManager, handleGetFiles, pageLimit } = useFileManagerContext();

  const { showFilters, setShowFilters } = useFilterBreakpoint();

  // For case files, firm documents, client files, and lead files, show old simple filter layout exactly like staging
  if (isCaseFileManager || isFirmDocuments || isClientFileManager || isLeadFileManager) {
    return (
      <>
        <div className="d-block d-lg-none w-100">
          <Button
            className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            aria-controls="file-filter-section"
            label="Filters"
          >
            <span
              style={{
                fontSize: 18,
                transition: 'transform 0.2s',
                display: 'inline-block',
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <i className="fa fa-solid fa-angle-down"></i>
            </span>
          </Button>
        </div>
        <div
          className={`d-flex flex-wrap align-items-center gap-2 filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
          id="file-filter-section"
          aria-hidden={!showFilters}
        >
          <label className="text-bold black-600  me-2 filter-label">Filters:</label>
          {isClientFileManager && <CaseFilter />}
          <FileTypeFilter />
          <TagFilter />
          {(isCaseFileManager || isClientFileManager || isLeadFileManager) && <FileStatusFilter />}
          {isFirmDocuments && <ReadStatusFilter />}
          <ResetFilters />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="d-block d-lg-none w-100">
        <Button
          className="p-button p-component btn p-button-secondary outline w-100 filter-toggle-btn"
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          aria-controls="files-filter-section"
          label="Filters"
        >
          <span
            style={{
              fontSize: 18,
              transition: 'transform 0.2s',
              display: 'inline-block',
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <i className="fa fa-solid fa-angle-down"></i>
          </span>
        </Button>
      </div>
      <div
        className={`filter-section p-3 border-round-lg filter-wrap filter-toggle-section filter-section${showFilters ? ' show' : ''}`}
        id="files-filter-section"
        aria-hidden={!showFilters}
      >
        <div className="mb-4">
          <h6 className="text-bold black-600 filter-label">Filter Files</h6>
        </div>

        {/* First Row of Filters */}
        <div className="row mb-3 g-3">
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                {isFirmDocuments ? 'CASE' : 'UPLOADED BY'}
              </label>
              {isFirmDocuments ? <CaseFilter /> : <UploadedByFilter />}
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                PRACTICE AREA
              </label>
              <PracticeAreaFilter />
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                LEAD ATTORNEY
              </label>
              <LeadAttorneyFilter />
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                LEAD PARALEGAL
              </label>
              <LeadParalegalFilter />
            </div>
          </div>
        </div>

        {/* Second Row of Filters */}
        <div className="row mb-3 g-3">
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                READ STATUS
              </label>
              <ReadStatusFilter />
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                SIGNATURE STATUS
              </label>
              <SignatureStatusFilter />
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                FILE TYPE
              </label>
              <FileTypeFilter />
            </div>
          </div>
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                BY TAG
              </label>
              <TagFilter />
            </div>
          </div>
        </div>

        {/* Third Row of Filters */}
        <div className="row mb-4 g-3">
          <div className="col-lg-3 col-md-6 col-12">
            <div className="d-flex flex-column">
              <label className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' }}>
                DATE RANGE
              </label>
              <DateRangeFilter />
            </div>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="d-flex align-items-center gap-2">
          <button
            className="p-button p-button-primary"
            onClick={() => {
              // Refresh files with current filter state
              handleGetFiles(pageLimit);
            }}
          >
            Apply Filters
          </button>
          <ResetFilters />
        </div>
      </div>
    </>
  );
}

export default Filters;
